import http, { request } from './http';
import type {
  CalculatorFormula,
  FormulaImportAnalyzedItem,
  FormulaImportChatResult,
} from '@/types/calculatorFormula';

export interface CustomCalculatorFormulaRecord {
  formulaId: string;
  name: string;
  formula: CalculatorFormula;
  source?: string;
  updatedAt?: string;
  authorName?: string;
  authorUserId?: number;
  relation?: 'owner' | 'saved';
  owner?: boolean;
  shared?: boolean;
}

function pickStr(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function mapAnalyzedItem(raw: Record<string, unknown>): FormulaImportAnalyzedItem {
  const inputRaw = raw.input_params ?? raw.inputParams;
  const outputRaw = raw.output_params ?? raw.outputParams;
  return {
    id: pickStr(raw, 'id') || 'f1',
    name: pickStr(raw, 'name'),
    expression: pickStr(raw, 'expression'),
    principle: pickStr(raw, 'principle'),
    inputParams: Array.isArray(inputRaw) ? inputRaw.map(String) : [],
    outputParams: Array.isArray(outputRaw) ? outputRaw.map(String) : [],
    optimization: pickStr(raw, 'optimization'),
    complexityScore: Number(raw.complexity_score ?? raw.complexityScore ?? 0) || undefined,
  };
}

function mapChatResult(data: Record<string, unknown>): FormulaImportChatResult {
  const formulasRaw = data.formulas;
  const generatedRaw = data.generated_formulas ?? data.generatedFormulas;
  return {
    success: data.success !== false,
    phase: (pickStr(data, 'phase') as FormulaImportChatResult['phase']) || 'discuss',
    assistantReply: pickStr(data, 'assistant_reply', 'assistantReply'),
    conversationId: pickStr(data, 'conversation_id', 'conversationId'),
    documentText: pickStr(data, 'document_text', 'documentText'),
    fileName: pickStr(data, 'file_name', 'fileName'),
    message: pickStr(data, 'message'),
    formulas: Array.isArray(formulasRaw)
      ? formulasRaw.map((x) => mapAnalyzedItem(x as Record<string, unknown>))
      : undefined,
    generatedFormulas: Array.isArray(generatedRaw)
      ? (generatedRaw as CalculatorFormula[])
      : undefined,
  };
}

export async function fetchCustomCalculatorFormulas(): Promise<CustomCalculatorFormulaRecord[]> {
  const res = await request<{ success: boolean; data: CustomCalculatorFormulaRecord[] }>(
    http.get('/ai/calculator/custom-formulas'),
  );
  return res.data ?? [];
}

export async function fetchCustomCalculatorFormula(formulaId: string): Promise<CustomCalculatorFormulaRecord> {
  const res = await request<{ success: boolean; message?: string; data: CustomCalculatorFormulaRecord }>(
    http.get(`/ai/calculator/custom-formulas/${encodeURIComponent(formulaId)}`),
  );
  if (res.success === false) {
    throw new Error(res.message?.trim() || '加载公式失败');
  }
  return res.data;
}

function mapRecordToFormula(r: CustomCalculatorFormulaRecord): CalculatorFormula {
  const base = { ...r.formula, id: r.formulaId || r.formula.id, isCustom: true };
  return {
    ...base,
    authorName: r.authorName || r.formula.authorName,
    owner: r.owner,
    shared: r.shared,
  };
}

export { mapRecordToFormula };

export async function saveCustomCalculatorFormula(payload: {
  formulaId: string;
  name: string;
  formula: CalculatorFormula;
  source?: string;
}): Promise<CustomCalculatorFormulaRecord> {
  const res = await request<{ success: boolean; data: CustomCalculatorFormulaRecord }>(
    http.post('/ai/calculator/custom-formulas', payload),
  );
  return res.data;
}

export async function saveCustomCalculatorFormulasBatch(
  items: Array<{ formulaId: string; name: string; formula: CalculatorFormula; source?: string }>,
): Promise<CustomCalculatorFormulaRecord[]> {
  const res = await request<{ success: boolean; data: CustomCalculatorFormulaRecord[] }>(
    http.post('/ai/calculator/custom-formulas/batch', items),
  );
  return res.data ?? [];
}

export async function deleteCustomCalculatorFormula(formulaId: string): Promise<void> {
  await request<{ success: boolean; data: unknown }>(
    http.delete(`/ai/calculator/custom-formulas/${encodeURIComponent(formulaId)}`),
  );
}

export async function uploadAndAnalyzeFormulaImport(
  file: File,
  conversationId?: string,
): Promise<FormulaImportChatResult> {
  const form = new FormData();
  form.append('file', file);
  if (conversationId) {
    form.append('conversationId', conversationId);
  }
  const res = await request<{ success: boolean; data: Record<string, unknown> }>(
    http.post('/ai/calculator/formula-import/upload-analyze', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    }),
  );
  return mapChatResult(res.data ?? {});
}

export async function analyzeFormulaImport(payload: {
  documentText: string;
  fileName?: string;
  conversationId?: string;
}): Promise<FormulaImportChatResult> {
  const res = await request<{ success: boolean; data: Record<string, unknown> }>(
    http.post('/ai/calculator/formula-import/analyze', payload),
  );
  return mapChatResult(res.data ?? {});
}

export async function analyzeManualFormulaImport(payload: {
  formulaName: string;
  remark?: string;
  description?: string;
  conversationId?: string;
}): Promise<FormulaImportChatResult> {
  const res = await request<{ success: boolean; data: Record<string, unknown> }>(
    http.post('/ai/calculator/formula-import/analyze-manual', payload, { timeout: 300000 }),
  );
  return mapChatResult(res.data ?? {});
}

export async function chatFormulaImport(payload: {
  conversationId: string;
  message: string;
  documentText?: string;
  fileName?: string;
  history?: Array<{ role: string; content: string }>;
}): Promise<FormulaImportChatResult> {
  const res = await request<{ success: boolean; data: Record<string, unknown> }>(
    http.post('/ai/calculator/formula-import/chat', payload),
  );
  return mapChatResult(res.data ?? {});
}
